/* eslint-disable perfectionist/sort-objects */
import { type ResponsiveValue } from 'styled-system';

const headlineTypographyStyles = {
  firstLevelHeadline: {
    fontFamily: 'headline',
    fontSize: '5.8125rem',
    fontWeight: 'bold',
    lineHeight: 1.3,
  },
  secondLevelHeadline: {
    fontFamily: 'headline',
    fontSize: '3.625rem',
    fontWeight: 'bold',
    lineHeight: 1.3,
  },
  thirdLevelHeadline: {
    fontFamily: 'headline',
    fontSize: '3rem',
    fontWeight: 'bold',
    letterSpacing: -0.75,
    lineHeight: 1.25,
  },
  fourthLevelHeadline: {
    fontFamily: 'headline',
    fontSize: '2.125rem',
    fontWeight: 'bold',
    letterSpacing: -0.5,
    lineHeight: 5,
  },
  fifthLevelHeadline: {
    fontFamily: 'headline',
    fontSize: '1.4375rem',
    fontWeight: 'semiBold',
    letterSpacing: -0.25,
    lineHeight: 1.4,
  },
  sixthLevelHeadline: {
    fontFamily: 'headline',
    fontSize: '1.1875rem',
    fontWeight: 'medium',
    lineHeight: 1.47,
  },
};

const universalSubtitleStyle = {
  fontFamily: 'body',
  fontWeight: 'medium',
};

const subtitleTypographyStyles = {
  subtitleRegular: {
    ...universalSubtitleStyle,
    fontSize: '1rem',
    lineHeight: 1.5,
  },
  subtitleSmall: {
    ...universalSubtitleStyle,
    fontSize: '.875rem',
    lineHeight: 1.42,
  },
};

const buttonTypographyStyles = {
  buttonLabelRegular: {
    fontFamily: 'body',
    fontSize: '1rem',
    fontWeight: 'semiBold',
    lineHeight: 1.5,
  },
};

const universalBodyStyle = {
  fontFamily: 'body',
  fontWeight: 'regular',
};

const bodyTypographyStyles = {
  bodyRegular: {
    ...universalBodyStyle,
    fontSize: '1rem',
    lineHeight: 1.5,
  },
  bodySmall: {
    ...universalBodyStyle,
    fontSize: '0.875rem',
    letterSpacing: 0.15,
    lineHeight: 1.42,
  },
};

const universalCaptionStyle = {
  fontFamily: 'body',
  fontSize: '.75rem',
  letterSpacing: 0.15,
  lineHeight: 1.333,
};

const captionTypographyStyles = {
  captionRegular: {
    ...universalCaptionStyle,
    fontWeight: 400,
  },
  captionBold: {
    ...universalCaptionStyle,
    fontWeight: 'bold',
  },
  captionMedium: {
    ...universalCaptionStyle,
    fontWeight: 500,
  },
  captionSmall: {
    ...universalCaptionStyle,
    fontWeight: 'regular',
  },
};

const listTypographyStyles = {
  listItem: {
    ...universalBodyStyle,
    fontSize: '1.1875rem',
    lineHeight: 1.47,
    color: 'gray90',
  },
};

const monoStyles = {
  monoRegular: {
    fontFeatureSettings: '"tnum"',
    fontFamily: 'mono',
    fontWeight: 400,
  },
};
const universalOverlineStyles = {
  fontFamily: 'body',
  fontSize: '.625rem',
  lineHeight: 1.6,
  textTransform: 'uppercase',
};

const otherTypographyStyles = {
  avatarCardProjectCover: {
    fontFamily: 'body',
    fontSize: '.825rem',
    fontWeight: 'bold',
    lineHeight: 1.28,
    letterSpacing: -0.19,
  },
  overline: {
    ...universalOverlineStyles,
    fontWeight: '500',
    letterSpacing: 1.2,
  },
  /**
   * New overline style added for Paid Projects Tab based on conversation here
   * https://www.figma.com/file/4XtegrAcGTIz9Df6hUJa7s?node-id=36748:175371#96340230
   * - @imbhargav5
   */
  overlineBold: {
    ...universalOverlineStyles,
    fontWeight: 'semiBold',
    letterSpacing: 0.5,
  },
  brandItalic: {
    fontFamily: 'Inter',
    fontStyle: 'italic',
    fontWeight: 400,
  },

  // strong headline
  strongHeadline: {
    fontFamily: 'headline',
    fontStyle: 'normal',
    fontWeight: 900,
    fontSize: '5.9375rem',
    lineHeight: 1.28,
    letterSpacing: '-0.20rem',
  },
  overlineSemibold: {
    fontSize: '10px',
    fontWeight: '600',
    lineHeight: '1.6',
    letterSpacing: '1.2px',
    textTransform: 'uppercase',
  },
  overlineRegular: {
    fontFamily: 'body',
    fontWeight: 500,
    fontSize: '10px',
    lineHeight: '16px',
    letterSpacing: '1.2px',
    textTransform: 'uppercase',
  },

  // Screen Reader Only
  screenReaderOnly: {
    border: 0,
    clip: 'rect(1px, 1px, 1px, 1px)',
    clipPath: 'inset(50%)',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    width: '1px',
  },
};

const newStyles = {
  'text-5xl-medium': {
    fontFamily: 'body',
    fontSize: '93px',
    fontWeight: 500,
    lineHeight: '120px',
    letterSpacing: '-2.5px',
  },
  'text-4xl-medium': {
    fontFamily: 'body',
    fontSize: '58px',
    fontWeight: 500,
    lineHeight: '72px',
    letterSpacing: '-1px',
  },
  'text-3xl-medium': {
    fontFamily: 'body',
    fontSize: '48px',
    fontWeight: 500,
    lineHeight: '60px',
    letterSpacing: '-0.75px',
  },
  'text-3xl-regular': {
    fontFamily: 'body',
    fontSize: '48px',
    fontWeight: 400,
    lineHeight: '60px',
    letterSpacing: '-0.75px',
  },
  'text-2xl-medium': {
    fontFamily: 'body',
    fontSize: '34px',
    fontWeight: 500,
    lineHeight: '44px',
    letterSpacing: '-0.5px',
  },
  'text-2xl-regular': {
    fontFamily: 'body',
    fontSize: '34px',
    fontWeight: 400,
    lineHeight: '44px',
    letterSpacing: '-0.5px',
  },
  'text-xl-medium': {
    fontFamily: 'body',
    fontSize: '23px',
    fontWeight: 500,
    lineHeight: '32px',
    letterSpacing: '-0.25px',
  },
  'text-lg-medium': {
    fontFamily: 'body',
    fontSize: '19px',
    fontWeight: 500,
    lineHeight: '28px',
  },
  'text-base-regular': {
    fontFamily: 'body',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
  },
  'text-base-medium': {
    fontFamily: 'body',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '24px',
  },
  'text-base-semi-bold': {
    fontFamily: 'body',
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '24px',
    letterSpacing: '-0.15px',
  },
  'text-medium-regular': {
    fontFamily: 'body',
    fontSize: '15px',
    fontWeight: 400,
    lineHeight: '24px',
  },
  'text-medium-medium': {
    fontFamily: 'body',
    fontSize: '15px',
    fontWeight: 500,
    lineHeight: '24px',
  },
  'text-medium-semi-bold': {
    fontFamily: 'body',
    fontSize: '15px',
    fontWeight: 600,
    lineHeight: '24px',
  },
  'text-sm-regular': {
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.15px',
  },
  'text-sm-medium': {
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px',
    letterSpacing: '0.15px',
  },
  'text-sm-semi-bold': {
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '20px',
  },
  'text-xs-regular': {
    fontFamily: 'body',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '16px',
    letterSpacing: '0.15px',
  },
  'text-xs-semi-bold': {
    fontFamily: 'body',
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: '16px',
    letterSpacing: '0.15px',
  },
  'text-ol-medium': {
    fontFamily: 'body',
    fontSize: '10px',
    fontWeight: 500,
    lineHeight: '16px',
    letterSpacing: '1.2px',
    textTransform: 'uppercase',
  },
  'text-ol-semi-bold': {
    fontFamily: 'body',
    fontSize: '10px',
    fontWeight: 600,
    lineHeight: '16px',
    letterSpacing: '1.2px',
    textTransform: 'uppercase',
  },
  'codeblock-regular': {
    fontFamily: 'mono',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
  },
};

export const textStyles = {
  ...headlineTypographyStyles,
  ...subtitleTypographyStyles,
  ...buttonTypographyStyles,
  ...bodyTypographyStyles,
  ...captionTypographyStyles,
  ...listTypographyStyles,
  ...monoStyles,
  ...otherTypographyStyles,
  ...newStyles,
};

export type TextStyles = ResponsiveValue<keyof typeof textStyles>;
